import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Mail,
  MailOpen,
  Clock,
  AlertCircle,
  CheckCircle,
  Paperclip,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useReceivedMessages } from "@/hooks/use-messagerie";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const MessagesReceivedList = ({ onMessageSelect }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");

  const limit = 10;

  const {
    data: messagesData,
    isLoading,
    error,
  } = useReceivedMessages({
    page: currentPage,
    limit,
    search,
    statut: statusFilter === "all" ? "" : statusFilter,
    priorite: priorityFilter === "all" ? "" : priorityFilter,
  });

  const messages = messagesData?.data?.messages || [];
  const pagination = messagesData?.data?.pagination || {};

  // Fonctions utilitaires
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgente":
        return "destructive";
      case "importante":
        return "secondary";
      case "normale":
      default:
        return "outline";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "traite":
        return "default";
      case "en_attente":
        return "secondary";
      case "envoye":
      default:
        return "outline";
    }
  };

  const getStatusIcon = (message) => {
    if (message.statut === "traite") {
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    }
    if (message.statut === "en_attente") {
      return <Clock className="h-4 w-4 text-orange-600" />;
    }
    if (message.priorite === "urgente") {
      return <AlertCircle className="h-4 w-4 text-red-600" />;
    }
    return message.date_lecture ? (
      <MailOpen className="h-4 w-4 text-muted-foreground" />
    ) : (
      <Mail className="h-4 w-4 text-blue-600" />
    );
  };

  // Gestion des filtres
  const handleSearchChange = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handlePriorityFilterChange = (value) => {
    setPriorityFilter(value);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setPriorityFilter("all");
    setCurrentPage(1);
  };

  // Gestion de la pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setCurrentPage(newPage);
    }
  };

  if (error) {
    return (
      <div className="text-center p-6">
        <p className="text-destructive">
          Erreur lors du chargement des messages
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          {error.response?.data?.message || error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par objet, expéditeur..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="envoye">Envoyé</SelectItem>
              <SelectItem value="en_attente">En attente</SelectItem>
              <SelectItem value="traite">Traité</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={priorityFilter}
            onValueChange={handlePriorityFilterChange}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Priorité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes priorités</SelectItem>
              <SelectItem value="normale">Normale</SelectItem>
              <SelectItem value="importante">Importante</SelectItem>
              <SelectItem value="urgente">Urgente</SelectItem>
            </SelectContent>
          </Select>

          {(search || statusFilter || priorityFilter) && (
            <Button variant="outline" onClick={handleResetFilters}>
              <Filter className="h-4 w-4 mr-2" />
              Réinitialiser
            </Button>
          )}
        </div>
      </div>

      {/* Table des messages */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead>Expéditeur</TableHead>
              <TableHead>Objet</TableHead>
              <TableHead className="w-24">Priorité</TableHead>
              <TableHead className="w-24">Statut</TableHead>
              <TableHead className="w-32">Date</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Skeleton loading
              [...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={7} className="h-12">
                    <div className="animate-pulse bg-muted h-4 rounded"></div>
                  </TableCell>
                </TableRow>
              ))
            ) : messages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <Mail className="h-8 w-8 text-muted-foreground" />
                    <p className="text-muted-foreground">
                      Aucun message trouvé
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              messages.map((message) => (
                <TableRow
                  key={message.id}
                  className={cn(
                    "cursor-pointer hover:bg-muted/50 transition-colors",
                    !message.date_lecture &&
                      "bg-blue-50/30 border-l-4 border-l-blue-500"
                  )}
                  onClick={() => onMessageSelect(message.id)}
                >
                  <TableCell>{getStatusIcon(message)}</TableCell>
                  <TableCell>
                    <div className="font-medium">
                      {message.employe?.prenom} {message.employe?.nom}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {message.employe?.email}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div
                      className={cn(
                        "font-medium line-clamp-1",
                        !message.date_lecture && "font-semibold"
                      )}
                    >
                      {message.objet}
                    </div>
                    {message.description && (
                      <div className="text-sm text-muted-foreground line-clamp-1">
                        {message.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getPriorityColor(message.priorite)}>
                      {message.priorite}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusColor(message.statut)}>
                      {message.statut?.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {format(new Date(message.date_envoi), "dd MMM", {
                        locale: fr,
                      })}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {format(new Date(message.date_envoi), "HH:mm")}
                    </div>
                  </TableCell>
                  <TableCell>
                    {message.has_attachment && (
                      <Paperclip className="h-4 w-4 text-muted-foreground" />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Page {pagination.page} sur {pagination.pages} ({pagination.total}{" "}
            messages au total)
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Précédent
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.pages}
            >
              Suivant
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesReceivedList;
